import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Paper,
  Typography, Box, TextField, Button, CircularProgress,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Alert, Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

function PontoHistorico() {
  const { user } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split('T')[0]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editTipo, setEditTipo] = useState('');
  const [editDataHora, setEditDataHora] = useState('');
  const [editObservacoes, setEditObservacoes] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [funcionarios, setFuncionarios] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addPessoaId, setAddPessoaId] = useState('');
  const [addTipo, setAddTipo] = useState('entrada');
  const [addDataHora, setAddDataHora] = useState('');
  const [addObservacoes, setAddObservacoes] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addErro, setAddErro] = useState('');
  const [feedback, setFeedback] = useState('');

  const limit = 20;

  const fetchRegistros = useCallback(() => {
    setLoading(true);
    const params = { page, limit };
    if (dataInicio) params.data_inicio = `${dataInicio} 00:00:00`;
    if (dataFim) params.data_fim = `${dataFim} 23:59:59`;

    api.get('/ponto', { params })
      .then((response) => {
        setRegistros(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
      })
      .catch((err) => {
        console.error('Erro ao buscar histórico de ponto:', err);
      })
      .finally(() => setLoading(false));
  }, [page, dataInicio, dataFim]);

  useEffect(() => {
    fetchRegistros();
  }, [fetchRegistros]);

  const formatDataHora = (dataHora) => {
    if (!dataHora) return '-';
    const d = new Date(dataHora);
    return d.toLocaleString('pt-BR');
  };

  const totalPages = Math.ceil(totalCount / limit);

  const podeGerenciar = user?.tipo_usuario === 'admin' || user?.tipo_usuario === 'sindico';

  useEffect(() => {
    if (!podeGerenciar) return;
    api.get('/ponto/portaria-funcionarios')
      .then((response) => setFuncionarios(response.data.data || []))
      .catch((err) => console.error('Erro ao buscar funcionários:', err));
  }, [podeGerenciar]);

  const handleAddOpen = () => {
    const now = new Date();
    const localISO = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setAddPessoaId('');
    setAddTipo('entrada');
    setAddDataHora(localISO);
    setAddObservacoes('');
    setAddErro('');
    setAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setAddDialogOpen(false);
  };

  const handleAddSave = () => {
    if (!addPessoaId || !addTipo || !addDataHora) {
      setAddErro('Preencha funcionário, tipo e data/hora.');
      return;
    }
    setAddSaving(true);
    setAddErro('');
    api.post('/ponto/adicionar', {
      pessoa_id: parseInt(addPessoaId),
      tipo: addTipo,
      data_hora: addDataHora,
      observacoes: addObservacoes,
    })
      .then(() => {
        setAddDialogOpen(false);
        setFeedback('Registro de ponto criado com sucesso.');
        fetchRegistros();
      })
      .catch((err) => {
        setAddErro(err.response?.data?.message || 'Erro ao criar registro de ponto.');
      })
      .finally(() => setAddSaving(false));
  };

  const handleEditOpen = (record) => {
    setEditingRecord(record);
    setEditTipo(record.tipo);
    const d = new Date(record.data_hora);
    const localISO = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setEditDataHora(localISO);
    setEditObservacoes(record.observacoes || '');
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingRecord(null);
  };

  const handleEditSave = () => {
    if (!editingRecord) return;
    setEditSaving(true);
    const payload = {
      tipo: editTipo,
      data_hora: editDataHora,
      observacoes: editObservacoes,
    };
    api.put(`/ponto/${editingRecord.id}`, payload)
      .then(() => {
        handleEditClose();
        fetchRegistros();
      })
      .catch((err) => {
        console.error('Erro ao atualizar ponto:', err);
      })
      .finally(() => setEditSaving(false));
  };

  const handleDelete = (record) => {
    if (!window.confirm(`Tem certeza que deseja excluir o registro de ${record.pessoa_nome || 'ponto'}?`)) return;
    api.delete(`/ponto/${record.id}`)
      .then(() => {
        fetchRegistros();
      })
      .catch((err) => {
        console.error('Erro ao excluir ponto:', err);
      });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Histórico de registros
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <TextField
          label="Data início"
          type="date"
          value={dataInicio}
          onChange={(e) => { setDataInicio(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Data fim"
          type="date"
          value={dataFim}
          onChange={(e) => { setDataFim(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="outlined" onClick={fetchRegistros} size="small">
          Atualizar
        </Button>
        {podeGerenciar && (
          <Button
            variant="contained"
            onClick={handleAddOpen}
            size="small"
            startIcon={<AddIcon />}
          >
            Adicionar registro
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : registros.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Nenhum registro encontrado no período.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Funcionário</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Observações</TableCell>
                  {podeGerenciar && <TableCell>Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {registros.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{formatDataHora(r.data_hora)}</TableCell>
                    <TableCell>{r.pessoa_nome || '-'}</TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          color: r.tipo === 'entrada' ? 'success.main' : 'warning.main',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                        }}
                      >
                        {r.tipo}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.observacoes || '-'}</TableCell>
                    {podeGerenciar && (
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEditOpen(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(r)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </Button>
              <Typography sx={{ alignSelf: 'center', px: 2 }}>
                {page + 1} de {totalPages}
              </Typography>
              <Button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                Próximo
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Editar registro de ponto</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Tipo"
              value={editTipo}
              onChange={(e) => setEditTipo(e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="entrada">Entrada</MenuItem>
              <MenuItem value="saida">Saída</MenuItem>
            </TextField>
            <TextField
              label="Data/Hora"
              type="datetime-local"
              value={editDataHora}
              onChange={(e) => setEditDataHora(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label="Observações"
              value={editObservacoes}
              onChange={(e) => setEditObservacoes(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving || !editTipo || !editDataHora}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDialogOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar registro de ponto</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Use esta opção para registrar o ponto de um funcionário que esqueceu de bater.
            </Typography>
            {addErro && (
              <Alert severity="error" onClose={() => setAddErro('')}>
                {addErro}
              </Alert>
            )}
            <TextField
              select
              label="Funcionário"
              value={addPessoaId}
              onChange={(e) => setAddPessoaId(e.target.value)}
              size="small"
              fullWidth
            >
              {funcionarios.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.nome}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Tipo"
              value={addTipo}
              onChange={(e) => setAddTipo(e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="entrada">Entrada</MenuItem>
              <MenuItem value="saida">Saída</MenuItem>
            </TextField>
            <TextField
              label="Data/Hora"
              type="datetime-local"
              value={addDataHora}
              onChange={(e) => setAddDataHora(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label="Observações"
              value={addObservacoes}
              onChange={(e) => setAddObservacoes(e.target.value)}
              size="small"
              fullWidth
              placeholder="Registro manual (esquecimento)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAddSave}
            disabled={addSaving || !addPessoaId || !addTipo || !addDataHora}
          >
            {addSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={() => setFeedback('')}
        message={feedback}
      />
    </Box>
  );
}

export default PontoHistorico;
